import React, { useState, useCallback } from 'react'
import { IconEditor } from '@/tiptap/tiptap-ui/icon-editor'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Info,
  MoreHorizontal,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconComponent } from '@/components/ui'
import { Separator } from '@/components/ui'
import { Switch } from '@/components/ui'
import { Button } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { AdmonitionAttributes, AdmonitionType } from './admonition-extension'
import './index.scss'

interface AdmonitionNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

// Icon map for each admonition type
const AdmonitionIcons = {
  note: AlertCircle,
  tip: Check,
  info: Info,
  warning: AlertCircle,
  danger: AlertTriangle,
}

// Color map for each admonition type
const AdmonitionColors = {
  note: {
    bg: 'bg-[#0187F0]/4 dark:bg-[#0070FD]/9',
    border: 'border-[#0179F4]/29 dark:border-[#0191FF]/48',
    title: 'text-[#0072DE] dark:text-[#6ABAFF]',
    icon: 'text-[#0072DE] dark:text-[#6ABAFF]',
    description: 'text-[#0072DE] dark:text-[#6ABAFF]',
  },
  tip: {
    bg: 'bg-[#009400]/4 dark:bg-[#01FE93]/4',
    border: 'border-[#008810]/28 dark:border-[#19FEAD]/29',
    title: 'text-[#00834D] dark:text-[#3DD68C]',
    icon: 'text-[#00834D] dark:text-[#3DD68C]',
    description: 'text-[#00834D] dark:text-[#3DD68C]',
  },
  info: {
    bg: 'bg-[##71717A]/5 dark:bg-[#71717A]/5',
    border: 'border-[#3F3F46]/20 dark:border-[#D4D4D8]/20',
    title: 'text-zinc-700 dark:text-zinc-300',
    icon: 'text-zinc-500 dark:text-zinc-300',
    description: 'text-zinc-700 dark:text-zinc-300',
  },
  warning: {
    bg: 'bg-[#ECCD01]/8 dark:bg-[#FFA100]/5',
    border: 'border-[#E9AC01]/50 dark:border-[#FFA309]/30',
    title: 'text-[#AD6200] dark:text-[#FFCA16]',
    icon: 'text-[#AD6200] dark:text-[#FFCA16]',
    description: 'text-[#AD6200] dark:text-[#FFCA16]',
  },
  danger: {
    bg: 'bg-[#F20101]/3 dark:bg-[#EF4444]/5',
    border: 'border-[#E90101]/25 dark:border-[#FF4D4D]/28',
    title: 'text-[#CF2D32] dark:text-[#FF6767]',
    icon: 'text-[#CF2D32] dark:text-[#FF6767]',
    description: 'text-[#CF2D32] dark:text-[#FF6767]',
  },
}

export function AdmonitionNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: AdmonitionNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const attrs = node.attrs as AdmonitionAttributes
  const title = attrs.title || ''
  const showTitle = attrs.showTitle !== false // Default to true
  const admonitionType = (attrs.admonitionType || 'note') as AdmonitionType
  const customIcon = attrs.icon || 'sparkles'
  const iconType = attrs.iconType || 'REGULAR'
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon !== false // Default to true
  const useCustomIcon = attrs.useCustomIcon || false

  const Icon = AdmonitionIcons[admonitionType]
  const colors = AdmonitionColors[admonitionType] ?? AdmonitionColors.note

  // Temporary state for popover editing
  const [tempTitle, setTempTitle] = useState(title)
  const [tempShowTitle, setTempShowTitle] = useState(showTitle)
  const [tempType, setTempType] = useState(admonitionType)
  const [tempCustomIcon, setTempCustomIcon] = useState(customIcon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempUseCustomIcon, setTempUseCustomIcon] = useState(useCustomIcon)

  // Function to save settings from popover
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<AdmonitionAttributes> = {}

    if (tempTitle.trim() !== title) {
      newAttributes.title = tempTitle.trim() || ''
    }

    if (tempShowTitle !== showTitle) {
      newAttributes.showTitle = tempShowTitle
    }

    if (tempType !== admonitionType) {
      newAttributes.admonitionType = tempType
    }

    if (tempCustomIcon !== customIcon) {
      newAttributes.icon = tempCustomIcon
    }

    if (tempIconType !== iconType) {
      newAttributes.iconType = tempIconType
    }

    if (tempIconColor !== iconColor) {
      newAttributes.iconColor = tempIconColor
    }

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
    }

    if (tempUseCustomIcon !== useCustomIcon) {
      newAttributes.useCustomIcon = tempUseCustomIcon
    }

    if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }

    setIsPopoverOpen(false)
  }, [
    tempTitle,
    title,
    tempShowTitle,
    showTitle,
    tempType,
    admonitionType,
    tempCustomIcon,
    customIcon,
    tempIconType,
    iconType,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    tempUseCustomIcon,
    useCustomIcon,
    updateAttributes,
  ])

  // Function to cancel settings changes
  const cancelSettings = useCallback(() => {
    setTempTitle(title)
    setTempShowTitle(showTitle)
    setTempType(admonitionType)
    setTempCustomIcon(customIcon)
    setTempIconType(iconType)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempUseCustomIcon(useCustomIcon)
    setIsPopoverOpen(false)
  }, [
    title,
    showTitle,
    admonitionType,
    customIcon,
    iconType,
    iconColor,
    showIcon,
    useCustomIcon,
  ])

  // Function to open settings popover
  const openSettings = useCallback(() => {
    setTempTitle(title)
    setTempShowTitle(showTitle)
    setTempType(admonitionType)
    setTempCustomIcon(customIcon)
    setTempIconType(iconType)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempUseCustomIcon(useCustomIcon)
    setIsPopoverOpen(true)
  }, [
    title,
    showTitle,
    admonitionType,
    customIcon,
    iconType,
    iconColor,
    showIcon,
    useCustomIcon,
  ])

  // Handle delete admonition
  const handleDeleteAdmonition = useCallback(() => {
    deleteNode()
  }, [deleteNode])

  return (
    <NodeViewWrapper
      className={cn(
        'group relative !my-5 rounded-md border px-5.5 py-4.5',
        colors.bg,
        colors.border,
        selected && 'ProseMirror-selectednode'
      )}
      data-type='Admonition'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='flex items-start'>
        <div className={cn('mr-3', colors.icon)} contentEditable={false}>
          <div className={`mt-[2px] py-px`}>
            {showIcon &&
              (useCustomIcon ? (
                <IconComponent
                  iconName={customIcon}
                  iconType={iconType}
                  iconColor={iconColor}
                  size={18}
                  className='transition-colors'
                />
              ) : (
                <Icon className='h-[18px] w-[18px] [&_svg]:h-[18px] [&_svg]:w-[18px]' />
              ))}
          </div>
        </div>
        <div className='w-full'>
          {showTitle && title && (
            <div className='mb-1.5' contentEditable={false}>
              <span
                className={cn(
                  'text-sm leading-[18px] font-semibold',
                  colors.title
                )}
              >
                {title}
              </span>
            </div>
          )}

          <div
            className={cn(
              'prose node-Admonition-description ai-admonition-description prose-sm max-w-none [&_div]:!my-0 [&_div]:p-0 [&_div]:text-sm [&_div]:!leading-5',
              colors.description
            )}
          >
            <NodeViewContent />
          </div>
        </div>
      </div>

      {/* More button with Popover - appears on hover */}
      {editor.isEditable && (isHovered || isPopoverOpen) && (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='ghost'
              size='menuAction'
              className='absolute top-2 right-2 z-10 p-1'
              onClick={openSettings}
            >
              <MoreHorizontal className='text-foreground h-4 w-4' />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className='w-[340px] rounded-2xl border border-sidebar-border bg-white p-0 shadow-[0_8px_40px_0_rgba(0,0,0,0.08)]'
            align='end'
            side='bottom'
            sideOffset={8}
          >
            <div className='flex flex-col'>
              {/* Header */}
              <div className='flex items-center justify-between border-b border-sidebar-border px-6 py-5'>
                <h3 className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
                  Admonition Settings
                </h3>

                <Button
                  variant='ghost'
                  size='menuAction'
                  onClick={cancelSettings}
                  className='h-8 w-8 text-[#86888F] hover:bg-muted hover:text-[#43464D]'
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>

              <div className='space-y-4 px-6 py-6'>
                {/* Show Title Toggle */}
                <div className='flex items-center justify-between'>
                  <label className='text-[13px] leading-[1.2] font-normal text-[#86888F]'>
                    Show Title
                  </label>
                  <Switch
                    checked={tempShowTitle}
                    onCheckedChange={() => setTempShowTitle(!tempShowTitle)}
                  />
                </div>

                {/* Title Input */}
                <div className='flex flex-col gap-2'>
                  <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                    Title
                  </label>
                  <input
                    type='text'
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    placeholder='Enter title...'
                    className='placeholder:text-[#85878D] focus-visible:ring-ring h-[42px] w-full rounded-md border border-[#E3E1DD] px-3 text-[13px] leading-[1.2] font-normal text-[#43464D] transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                  />
                </div>

                {/* Type Selection */}
                <div>
                  <label className='mb-2 block text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                    Type
                  </label>
                  <div className='grid grid-cols-2 gap-2'>
                    {Object.entries(AdmonitionIcons).map(([type, TypeIcon]) => {
                      const isActive = type === tempType
                      const typeColor =
                        AdmonitionColors[type as AdmonitionType].icon

                      return (
                        <button
                          key={type}
                          className={cn(
                            'flex h-9 items-center rounded-lg border px-3 text-left text-[13px] transition-colors',
                            isActive
                              ? 'border-sidebar-primary/30 bg-sidebar-primary/10 text-sidebar-foreground'
                              : 'border-[#E3E1DD] bg-white text-[#43464D] hover:bg-muted'
                          )}
                          onClick={() => setTempType(type as AdmonitionType)}
                        >
                          <TypeIcon className={cn('mr-2 h-4 w-4', typeColor)} />
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
              {/* <Separator /> */}
              {/* Icon Settings */}
              {/* <div>
                <div className='mb-4 flex items-center justify-between'>
                  <label className='text-muted-foreground text-xs leading-4'>
                    Show Icon
                  </label>
                  <Switch
                    checked={tempShowIcon}
                    onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                  />
                </div>

                {tempShowIcon && (
                  <>
                    <div className='mb-5 flex items-center justify-between'>
                      <label className='text-muted-foreground text-xs leading-4'>
                        Use Custom Icon
                      </label>
                      <Switch
                        checked={tempUseCustomIcon}
                        onCheckedChange={() =>
                          setTempUseCustomIcon(!tempUseCustomIcon)
                        }
                      />
                    </div>

                    
                    {tempUseCustomIcon && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <IconEditor
                          selectedIcon={tempCustomIcon}
                          iconType={tempIconType}
                          iconColor={tempIconColor}
                          onIconChange={setTempCustomIcon}
                          onTypeChange={setTempIconType}
                          onColorChange={setTempIconColor}
                        />
                      </div>
                    )}
                  </>
                )}
              </div> */}
              <Separator className='m-0 bg-sidebar-border' />
              {/* Action Buttons */}
              <div className='flex items-center justify-between px-6 pt-[14px] pb-4'>
                <Button
                  variant='destructive'
                  className='h-9 bg-transparent px-0 text-[13px] leading-[1.1] font-normal text-[#C24646] shadow-none hover:bg-transparent hover:text-[#C24646]'
                  onClick={handleDeleteAdmonition}
                >
                  Delete
                </Button>
                <div className='flex items-center space-x-2'>
                  <Button
                    variant='outline'
                    className='h-9 w-24 rounded-lg border-[#E3E1DD] px-0 text-[13px] text-[#43464D] hover:bg-muted'
                    onClick={cancelSettings}
                  >
                    Cancel
                  </Button>
                  <Button
                    className='h-9 w-[120px] rounded-lg bg-sidebar-primary px-0 text-[13px] text-white hover:bg-[#6f8264]'
                    onClick={saveSettings}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </NodeViewWrapper>
  )
}
